import { useCallback, useEffect, useId, useRef } from 'react';
import { debounce } from 'es-toolkit';

import { AdornedInput, Form, Icon, IconButton, Spinner } from '@zillow/constellation-10';
import { IconCloseCircleFilled, IconSearchFilled } from '@zillow/constellation-icons';

export type ControlledSearchBoxProps = {
  'aria-label': string;
  value: string;
  onChange: (value: string) => void;
  onSearch?: (value: string) => void;
  placeholder?: string;
  onSubmit?: (value: string) => void;
  isSearching?: boolean;
  iconSize?: 'sm' | 'md' | 'lg';
  debounceMilliseconds?: number;
  disabled?: boolean;
  id?: string;
};

export const ControlledSearchBox = ({
  'aria-label': ariaLabel,
  value,
  onChange,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onSearch = () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onSubmit = () => {},
  iconSize: size = 'md',
  placeholder = 'Search',
  isSearching = false,
  debounceMilliseconds = 1_000,
  disabled = false,
  id,
}: ControlledSearchBoxProps) => {
  const isInitialRender = useRef(true);
  const generatedId = useId();
  const inputId = id ?? generatedId;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearchDebounced = useCallback(debounce(onSearch, debounceMilliseconds), [onSearch, debounceMilliseconds]);

  useEffect(() => {
    // Skip calling onSearch on initial render
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }

    handleSearchDebounced(value);

    return () => handleSearchDebounced.cancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const clearSearchText = () => {
    onChange('');
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (onSubmit) onSubmit(value);
  };

  const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') clearSearchText();
  };

  const renderAdornment = () =>
    value ? (
      <IconButton
        tone="neutral"
        emphasis="bare"
        size={size}
        title="Clear"
        onClick={clearSearchText}
        css={{ backgroundColor: 'transparent', width: '100%', height: '100%' }}
      >
        <IconCloseCircleFilled data-testid="ControlledSearchBox-CloseButton" />
      </IconButton>
    ) : (
      <Icon size={size}>
        <IconSearchFilled data-testid="ControlledSearchBox-SearchIcon" />
      </Icon>
    );

  // NOTE: asChild is not needed since pages consuming this component handles shallow route
  return (
    <Form onSubmit={handleSubmit}>
      <AdornedInput
        id={inputId}
        size={size}
        data-testid="ControlledSearchBox-Component"
        disabled={disabled}
        input={
          <AdornedInput.Input
            aria-label={ariaLabel}
            placeholder={placeholder}
            onChange={handleChange}
            onKeyUp={handleKeyUp}
            value={value}
          />
        }
        endAdornment={
          <AdornedInput.Adornment>
            {isSearching ? <Spinner size={size} data-testid="ControlledSearchBox-Spinner" /> : renderAdornment()}
          </AdornedInput.Adornment>
        }
      />
    </Form>
  );
};
